export type ProductSize =
  | string
  | {
      label?: string;
      size?: string;
      eu?: string;
      us?: string;
      uk?: string;
      stock?: number;
      buyable?: boolean;
    };

export interface SizePresenterProduct {
  brand?: string;
  name: string;
  size?: string;
  sizes?: ProductSize[] | string;
}

export interface SizeOption {
  rawValue: string;
  displayLabel: string;
  buyable: boolean;
}

export const COLLAPSED_SIZE_COUNT = 5;

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const ALPHA_SIZE_PATTERN = '(?:XXXL|XXL|XL|XS|XXS|S|M|L)';

const normalizeWhitespace = (value: string) => value.replace(/\s+/g, ' ').trim();

const getContextCandidates = (product: SizePresenterProduct): string[] => {
  const brand = normalizeWhitespace(product.brand ?? '');
  const name = normalizeWhitespace(product.name);
  const nameWithoutBrand =
    brand && name.toLowerCase().startsWith(brand.toLowerCase())
      ? normalizeWhitespace(name.slice(brand.length))
      : '';

  return [brand, name, nameWithoutBrand].filter(Boolean);
};

const stripLeadingContext = (value: string, candidates: string[]) => {
  let nextValue = value.trim();

  for (const candidate of candidates) {
    const trimmedCandidate = candidate.trim();
    if (!trimmedCandidate) continue;

    const directPrefix = new RegExp(`^${escapeRegExp(trimmedCandidate)}\\s*`, 'i');
    if (directPrefix.test(nextValue)) {
      nextValue = nextValue.replace(directPrefix, '').trim();
      continue;
    }

    const compactCandidate = trimmedCandidate.replace(/\s+/g, '');
    const compactValue = nextValue.replace(/\s+/g, '');
    if (compactValue.toLowerCase().startsWith(compactCandidate.toLowerCase())) {
      nextValue = compactValue.slice(compactCandidate.length).trim();
    }
  }

  return nextValue.trim();
};

const extractRegionalSize = (value: string, region: 'US' | 'UK' | 'EU') => {
  const match = value.match(
    new RegExp(
      `\\b${region}\\s*([0-9]+(?:[.,][0-9]+)?(?:\\s+[0-9]+\\/[0-9]+)?|[A-Z]+[0-9]+(?:\\/[0-9]+)?)\\b`,
      'i',
    ),
  );

  if (!match) return null;
  return `${region}${match[1].replace(/\s+/g, ' ').trim()}`;
};

const formatStructuredRegionalSize = (
  region: 'US' | 'UK' | 'EU',
  value: string | undefined,
): string | null => {
  const normalized = String(value ?? '')
    .replace(/\s+/g, ' ')
    .trim();
  if (!normalized) return null;

  const withoutPrefix = normalized.replace(new RegExp(`^${region}\\s*`, 'i'), '').trim();
  return `${region}${withoutPrefix}`;
};

export const splitSizeList = (value: string) =>
  value
    .split(/,\s+/)
    .map((part) => part.trim())
    .filter(Boolean);

export const normalizeRawSizeValue = (value: string, product: SizePresenterProduct) =>
  stripLeadingContext(value, getContextCandidates(product))
    .replace(/\s*-\s*/g, ' - ')
    .replace(/\s+/g, ' ')
    .trim();

const formatHybridShirtSize = (value: string): string | null => {
  const normalized = normalizeWhitespace(value);
  const match = normalized.match(
    new RegExp(`\\b(\\d{2,3})\\s*(?:-|/)\\s*(${ALPHA_SIZE_PATTERN})\\b`, 'i'),
  );
  if (!match) return null;

  return `${match[1]} / ${match[2].toUpperCase()}`;
};

const formatAlphaSize = (value: string): string | null => {
  const normalized = normalizeWhitespace(value);
  return new RegExp(`^${ALPHA_SIZE_PATTERN}$`, 'i').test(normalized)
    ? normalized.toUpperCase()
    : null;
};

export const buildDisplaySizeLabel = (
  rawValue: string,
  product: SizePresenterProduct,
  size?: Exclude<ProductSize, string>,
) => {
  const structuredUs = formatStructuredRegionalSize('US', size?.us);
  const structuredUk = formatStructuredRegionalSize('UK', size?.uk);
  const structuredEu = formatStructuredRegionalSize('EU', size?.eu);
  const structuredLeadingRegional = structuredUs ?? structuredUk;
  const structuredSize = String(size?.size ?? '').trim();

  if (structuredLeadingRegional && structuredEu) {
    return `${structuredLeadingRegional} / ${structuredEu}`;
  }
  if (structuredEu) return structuredEu;
  if (structuredLeadingRegional) return structuredLeadingRegional;
  if (structuredSize) {
    return (
      formatHybridShirtSize(structuredSize) ?? formatAlphaSize(structuredSize) ?? structuredSize
    );
  }

  const normalized = normalizeRawSizeValue(rawValue, product);
  const hybridShirtSize = formatHybridShirtSize(normalized);
  const alphaSize = formatAlphaSize(normalized);
  const usSize = extractRegionalSize(normalized, 'US');
  const ukSize = extractRegionalSize(normalized, 'UK');
  const euSize = extractRegionalSize(normalized, 'EU');
  const leadingRegionalSize = usSize ?? ukSize;

  if (leadingRegionalSize && euSize) return `${leadingRegionalSize} / ${euSize}`;
  if (euSize) return euSize;
  if (leadingRegionalSize) return leadingRegionalSize;
  if (hybridShirtSize) return hybridShirtSize;
  if (alphaSize) return alphaSize;

  return normalized;
};

export const normalizeSizeOption = (
  size: ProductSize,
  product: SizePresenterProduct,
): SizeOption | null => {
  const rawValue =
    typeof size === 'string' ? size.trim() : String(size.label ?? size.size ?? '').trim();
  if (!rawValue) return null;

  const buyable =
    typeof size === 'string'
      ? true
      : size.buyable !== false && (typeof size.stock !== 'number' || size.stock > 0);
  const displayLabel = buildDisplaySizeLabel(
    rawValue,
    product,
    typeof size === 'string' ? undefined : size,
  );

  return {
    rawValue,
    displayLabel: displayLabel || rawValue,
    buyable,
  };
};

export const parseSizeOptions = (product: SizePresenterProduct): SizeOption[] => {
  const deduped = new Map<string, SizeOption>();

  if (Array.isArray(product.sizes) && product.sizes.length > 0) {
    for (const size of product.sizes) {
      const normalized = normalizeSizeOption(size, product);
      if (!normalized) continue;
      deduped.set(normalized.rawValue, normalized);
    }

    return Array.from(deduped.values());
  }

  for (const rawValue of splitSizeList(String(product.size ?? ''))) {
    const normalized = normalizeSizeOption(rawValue, product);
    if (!normalized) continue;
    deduped.set(normalized.rawValue, normalized);
  }

  return Array.from(deduped.values());
};
